import { TextField } from '@mui/material';
import { GridRenderCellParams, GridRowId, GridValidRowModel } from '@mui/x-data-grid-pro';
import { NumericFormatDecimal } from '@procureai/ui-mui';
import Decimal from 'decimal.js';
import { isEmpty, isNil } from 'ramda';
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DefaultDataEditGridConfig } from '~/components/DataGridCustom';
import { defaultTextFieldProps } from '~/components/DataGridCustom/common/defaults';

interface DecimalTextFieldCellProps<R extends GridValidRowModel>
  extends GridRenderCellParams<R>, DefaultDataEditGridConfig {
  decimalScale?: number;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
}

export const DecimalTextFieldCell = <R extends GridValidRowModel & { id: GridRowId } = { id: GridRowId }>({
  colDef,
  getRowIndex,
  pathPrefix,
  row,
  decimalScale,
  prefix = '',
  suffix = '',
  languageNamespace,
  keyPrefix,
  textFieldProps,
  disabled,
}: DecimalTextFieldCellProps<R>) => {
  const { t: tPrefix } = useTranslation(languageNamespace, { keyPrefix });
  const p = [...(pathPrefix ?? []), getRowIndex?.(row.id), colDef.field];

  const InputComponent = useMemo(
    () => NumericFormatDecimal({ decimalScale, prefix, suffix }),
    [decimalScale, prefix, suffix],
  );
  return (
    <Controller
      name={p.join('.')}
      disabled={disabled}
      render={({ field, fieldState }) => {
        return (
          <TextField
            {...field}
            value={
              isNil(field.value) || isEmpty(field.value)
                ? ''
                : suffix === '%'
                  ? new Decimal(field.value).mul(100).toNumber()
                  : new Decimal(field.value).toNumber()
            }
            onClick={(e) => {
              e.stopPropagation();
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
            }}
            onChange={(e) => {
              const output = parseFloat(e.target.value);
              const val = isNaN(output)
                ? null
                : suffix === '%'
                  ? new Decimal(e.target.value).div(100).toNumber()
                  : output;
              field.onChange(val);
            }}
            label={tPrefix(colDef.field)}
            error={!!fieldState.error}
            {...defaultTextFieldProps}
            {...textFieldProps}
            slotProps={{
              input: {
                inputComponent: InputComponent as any,
              },

              inputLabel: { shrink: true },
            }}
          />
        );
      }}
    />
  );
};
